import { useRef } from 'react';
import { HttpTransportType, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { useConfig } from 'hooks/use-config';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { clients } from 'lib/https';
import { waitForSessionRestore } from 'features/auth/services/auth-service';
import { useNotificationStore } from '../state/store/common/useNotificationStore';
import { useNotificationFeedStore } from 'state/store/notifications/use-notification-feed-store';

interface NotificationChannelConfig {
  name?: string;
  notifyMethod: string;
}

const DEFAULT_CHANNELS: NotificationChannelConfig[] = [
  { name: 'Notification', notifyMethod: 'ReceiveNotification' },
];

// Exponential backoff capped at 30s. Never give up: a hub deploy or a long network drop
// would otherwise leave the session without notifications until a page reload.
const getRetryDelay = (retryCount: number) => Math.min(30000, 2000 * 2 ** retryCount);

// Notifications sent while disconnected are only persisted in the feed, so re-sync the badge.
const syncNotificationFeed = () => {
  useNotificationFeedStore
    .getState()
    .fetchAndSetNotification(0)
    .catch(() => undefined);
};

export function useNotification() {
  const { configurationSettings } = useConfig();
  const { handleNotification } = useNotificationStore();
  const disconnect = useRef<(() => Promise<void>) | null>(null);

  const logicBaseUrl = configurationSettings.paths.logicBaseUrl;
  const projectKey = configurationSettings.blocksKey;

  const fetchChannels = async (): Promise<NotificationChannelConfig[]> => {
    try {
      const url = `${logicBaseUrl}/Notification/Gets?page=0&pageSize=100&projectKey=${projectKey}`;
      const data = await clients.get<{ configurations?: NotificationChannelConfig[] }>(url, projectKey);
      if (data?.configurations && data.configurations.length > 0) {
        return data.configurations;
      }
      return DEFAULT_CHANNELS;
    } catch (error) {
      console.warn('Notification config fetch error, using default channels:', error);
      return DEFAULT_CHANNELS;
    }
  };

  const connect = async () => {
    if (!logicBaseUrl || !projectKey) {
      console.warn('Cannot connect to notification service: logic base url / project key missing');
      return;
    }

    // Scoped to this connect() call, so stopping it can't be undone by a newer connect().
    let isStopped = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    // Allow disconnect while channels are still loading, so logout/unmount can't leave a live connection.
    disconnect.current = async () => {
      isStopped = true;
    };

    const channels = await fetchChannels();
    if (isStopped) {
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${logicBaseUrl}/NotificationHub?x-blocks-key=${projectKey}`, {
        transport: HttpTransportType.WebSockets,
        // Wait for the boot refresh so the hub gets the in-memory token, not ''.
        // A refused restore signs the user out; the factory then returns ''.
        accessTokenFactory: async () => {
          await waitForSessionRestore().catch(() => undefined);
          return useAuthStore.getState().accessToken || '';
        },
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: ({ previousRetryCount }) => getRetryDelay(previousRetryCount),
      })
      .build();

    channels.forEach(({ name, notifyMethod }) => {
      connection.on(notifyMethod, (message: unknown) =>
        handleNotification(name ?? notifyMethod, message),
      );
    });

    let startRetryCount = 0;
    const startConnection = async () => {
      // Skip if another attempt already (re)connected or is in progress.
      if (isStopped || connection.state !== HubConnectionState.Disconnected) {
        return;
      }
      try {
        await connection.start();
        startRetryCount = 0;
        // Login and reconnect both miss anything persisted while the hub was down.
        syncNotificationFeed();
      } catch (error) {
        console.error('Notification SignalR connection failed', error);
        scheduleRestart();
      }
    };
    const scheduleRestart = () => {
      if (isStopped) {
        return;
      }
      clearTimeout(retryTimer);
      retryTimer = setTimeout(
        () => startConnection(),
        getRetryDelay(startRetryCount++)
      );
    };

    connection.onreconnected(syncNotificationFeed);
    // onclose also fires on our own stop(); scheduleRestart ignores it via isStopped.
    connection.onclose(scheduleRestart);

    disconnect.current = async () => {
      isStopped = true;
      clearTimeout(retryTimer);
      if (connection.state !== HubConnectionState.Disconnected) {
        await connection.stop();
      }
    };

    await startConnection();
  };

  return { connect, disconnect };
}
